import React from "react";
import { Link } from "react-router-dom";
import { PackagePlus, MapPin, Wallet, Repeat, ArrowRight, Star } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { CATEGORIES } from "../../constants/brand";
import { MOCK_ORDERS, MOCK_VENDORS } from "../../data/mock";

export default function CustomerHome() {
  const { user } = useAuth();
  const active = MOCK_ORDERS.filter((o) => o.status !== "delivered").slice(0, 3);

  return (
    <div className="space-y-6" data-testid="customer-home">
      {/* Greeting */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-950 p-6 text-white grain md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-lime-400">Welcome back</div>
            <h1 className="mt-2 font-display text-3xl font-bold" data-testid="customer-greeting">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
            <p className="mt-1 text-sm text-neutral-300">What do you want to print today?</p>
          </div>
          <Link to="/customer/new-order" className="btn-offset inline-flex items-center gap-2 rounded-md bg-lime-400 px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-lime-300" data-testid="ch-new-order">
            <PackagePlus size={16} /> New Order
          </Link>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid gap-4 md:grid-cols-4">
        <QA to="/customer/new-order" icon={PackagePlus} label="New Order" sub="Live bidding" testid="qa-new" />
        <QA to="/customer/track" icon={MapPin} label="Track Order" sub={`${active.length} active`} testid="qa-track" />
        <QA to="/customer/wallet" icon={Wallet} label="Wallet" sub="₹1,250" testid="qa-wallet" />
        <QA to="/customer/orders" icon={Repeat} label="Reorder" sub="Last: EP-2018" testid="qa-reorder" />
      </div>

      {/* Categories */}
      <section>
        <h2 className="font-display text-xl font-bold">What do you want to print?</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          {CATEGORIES.slice(0, 8).map((c) => (
            <Link key={c.key} to="/customer/new-order" className="group overflow-hidden rounded-lg border border-neutral-200 bg-white transition-transform hover:-translate-y-0.5" data-testid={`ch-cat-${c.key}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <div className="text-[10px] uppercase tracking-widest text-lime-300">From {c.price}</div>
                  <div className="font-display text-sm font-bold text-white">{c.label}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Vendors */}
      <section>
        <div className="flex items-end justify-between">
          <h2 className="font-display text-xl font-bold">Top vendors near you</h2>
          <Link to="/customer/new-order" className="text-xs font-semibold text-neutral-600 hover:text-neutral-900">See all →</Link>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          {MOCK_VENDORS.map((v) => (
            <div key={v.name} className="rounded-lg border border-neutral-200 bg-white p-3" data-testid={`ch-vendor-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <img src={v.img} alt="" className="h-24 w-full rounded-md object-cover" />
              <div className="mt-2 flex items-center justify-between">
                <div className="font-semibold">{v.name}</div>
                <div className="flex items-center gap-0.5 text-xs text-lime-600"><Star size={12} fill="currentColor" strokeWidth={0} />{v.rating}</div>
              </div>
              <div className="text-xs text-neutral-500">{v.area}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Active orders */}
      {active.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold">Your active orders</h2>
          <div className="mt-3 space-y-2">
            {active.map((o) => (
              <div key={o.id} className="flex flex-col justify-between gap-2 rounded-lg border border-neutral-200 bg-white p-4 md:flex-row md:items-center" data-testid={`ch-active-${o.id}`}>
                <div>
                  <div className="text-xs font-mono text-neutral-500">{o.id}</div>
                  <div className="font-semibold">{o.product}</div>
                  <div className="text-xs text-neutral-500">{o.eta}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={o.status} />
                  <Link to="/customer/track" className="rounded-md border border-neutral-900 px-3 py-1.5 text-xs font-bold hover:bg-neutral-950 hover:text-white">Track</Link>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function QA({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link to={to} className="group rounded-lg border border-neutral-200 bg-white p-5 transition-transform hover:-translate-y-0.5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-lime-400 text-neutral-950"><Icon size={16} /></div>
        <ArrowRight size={16} className="text-neutral-400 group-hover:text-neutral-900" />
      </div>
      <div className="mt-4 font-display text-lg font-bold">{label}</div>
      <div className="text-xs text-neutral-500">{sub}</div>
    </Link>
  );
}

export function StatusBadge({ status }) {
  const map = {
    placed: { c: "bg-neutral-100 text-neutral-800", t: "Placed" },
    printing: { c: "bg-amber-100 text-amber-800", t: "Printing" },
    ready: { c: "bg-blue-100 text-blue-800", t: "Ready" },
    delivered: { c: "bg-lime-100 text-lime-800", t: "Delivered" },
    "out for delivery": { c: "bg-orange-100 text-orange-800", t: "Out for delivery" },
    cancelled: { c: "bg-red-100 text-red-800", t: "Cancelled" },
  };
  const m = map[status] || map.placed;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${m.c}`}>{m.t}</span>;
}
